import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  BookOpen,
  Cloud,
  CloudOff,
  Database,
  FlaskConical,
  Lock,
  Pencil,
  Plus,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Trash2,
  UnlockKeyhole,
  Zap,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import { ApiError } from "@/api/client";
import {
  deleteTab,
  listTabs,
  regeneratePending,
  setReadOnly,
  validateAzure,
  validateSchema,
  type TabOut,
} from "@/api/tabs";
import { EmptyState } from "@/components/EmptyState";
import { Spinner } from "@/components/Spinner";
import { formatDateTime } from "@/lib/formatDate";
import { AdminHeader } from "@/pages/admin/components/AdminHeader";

export function TabsPage() {
  const qc = useQueryClient();
  const nav = useNavigate();
  const list = useQuery({ queryKey: ["tabs"], queryFn: listTabs });

  const remove = useMutation({
    mutationFn: (id: string) => deleteTab(id),
    onSuccess: () => {
      toast.success("Tab deleted.");
      void qc.invalidateQueries({ queryKey: ["tabs"] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Delete failed."),
  });

  const validate = useMutation({
    mutationFn: async (id: string) => {
      // Revalidate BOTH the MSSQL schema and the Azure Blob container in
      // parallel so one Revalidate click refreshes every "connected?" chip on
      // the card. The Azure call fails silently – the endpoint always returns
      // a report even when the tab has no Azure creds saved (reason:
      // not_configured), so it's safe to always fire it.
      const [schema, azure] = await Promise.allSettled([
        validateSchema(id),
        validateAzure(id),
      ]);
      return { schema, azure };
    },
    onSuccess: ({ schema, azure }) => {
      const schemaOk = schema.status === "fulfilled" && schema.value.schema_ok;
      const azureOk = azure.status === "fulfilled" && azure.value.azure_ok;
      toast[schemaOk && azureOk ? "success" : "error"](
        schemaOk && azureOk
          ? "Schema + attachments validated ✔"
          : !schemaOk && !azureOk
            ? "Schema invalid, attachments unreachable"
            : !schemaOk
              ? "Schema invalid"
              : "Attachments unreachable",
      );
      void qc.invalidateQueries({ queryKey: ["tabs"] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Validation failed."),
  });

  const toggleReadOnly = useMutation({
    mutationFn: ({ id, ro }: { id: string; ro: boolean }) => setReadOnly(id, ro),
    onSuccess: () => {
      toast.success("Updated.");
      void qc.invalidateQueries({ queryKey: ["tabs"] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : "Update failed."),
  });

  const regenerate = useMutation({
    mutationFn: (id: string) => regeneratePending(id),
    onSuccess: (r, tabId) => {
      if (r.status === "no_pending") {
        toast.info("No pending tickets to regenerate in this tab.");
      } else {
        toast.success(
          `Regeneration started for ${r.count} pending ticket(s). Drafts will fill in as they finish.`,
        );
      }
      // Backend cleared old drafts synchronously. Blow away any cached
      // grievance detail queries for this tab so an already-open ticket
      // page re-fetches and flips to the "not drafted yet" state.
      void qc.invalidateQueries({ queryKey: ["grievance", tabId] });
    },
    onError: (e) =>
      toast.error(e instanceof ApiError ? e.message : "Failed to start regeneration."),
  });

  return (
    <div className="min-h-screen flex flex-col">
      <AdminHeader title="Manage Exam Tabs" backTo="/admin" />
      <main className="w-full px-4 sm:px-6 lg:px-8 py-6 flex-1 flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold">Connected exam DBs</h2>
          <Link to="/admin/tabs/new" className="btn-primary text-sm px-3 py-1.5 inline-flex items-center gap-1.5">
            <Plus size={14} /> Add tab
          </Link>
        </div>

        {list.isLoading ? (
          <div className="card p-12 flex justify-center">
            <Spinner label="Loading tabs…" />
          </div>
        ) : list.isError ? (
          <div className="alert-danger">{(list.error as Error)?.message}</div>
        ) : list.data && list.data.items.length > 0 ? (
          <ul className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {list.data.items.map((t) => (
              <TabCard
                key={t.id}
                tab={t}
                onDelete={() =>
                  window.confirm(`Delete tab "${t.name}"? Its Qdrant collection is also removed.`) &&
                  remove.mutate(t.id)
                }
                onValidate={() => validate.mutate(t.id)}
                onRegenerate={() =>
                  window.confirm(
                    `Regenerate AI drafts for all Pending tickets in "${t.name}"?\n\n` +
                    "This will call Gemini once per pending ticket and overwrite any existing drafts. " +
                    "Runs in the background – you can leave this page.",
                  ) && regenerate.mutate(t.id)
                }
                onToggle={() => toggleReadOnly.mutate({ id: t.id, ro: !t.read_only })}
                onEdit={() => nav(`/admin/tabs/${t.id}/edit`)}
              />
            ))}
          </ul>
        ) : (
          <EmptyState
            title="No tabs yet."
            description="Create a tab to connect this app to an exam's govt SQL Server."
          />
        )}
      </main>
    </div>
  );
}

function TabCard({
  tab,
  onDelete,
  onValidate,
  onRegenerate,
  onToggle,
  onEdit,
}: {
  tab: TabOut;
  onDelete: () => void;
  onValidate: () => void;
  onRegenerate: () => void;
  onToggle: () => void;
  onEdit: () => void;
}) {
  return (
    <li className="card p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold truncate">{tab.name}</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 font-mono truncate">
            {tab.slug} · table {tab.mssql_table_name}
          </p>
        </div>
        <div className="flex gap-1">
          {tab.schema_valid ? (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 px-2 py-0.5 text-[11px] font-medium"
              title={`Last checked: ${formatDateTime(tab.schema_last_checked)}`}
            >
              <ShieldCheck size={11} /> Schema OK
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-2 py-0.5 text-[11px] font-medium">
              <Database size={11} /> Schema invalid
            </span>
          )}
          {tab.read_only && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-0.5 text-[11px] font-medium">
              <Lock size={11} /> Read-only
            </span>
          )}
          <AzureChip tab={tab} />
        </div>
      </div>
      <dl className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <dt className="text-slate-500 dark:text-slate-400">Qdrant collection</dt>
          <dd className="font-mono truncate">{tab.qdrant_collection}</dd>
        </div>
        <div>
          <dt className="text-slate-500 dark:text-slate-400">Vector points</dt>
          <dd className="font-mono">{tab.qdrant_point_count ?? "–"}</dd>
        </div>
      </dl>
      {!tab.schema_valid && <SchemaInvalidPanel report={tab.schema_report} />}
      {tab.azure_configured && tab.azure_valid === false && (
        <AzureInvalidPanel report={tab.azure_report} />
      )}
      {!tab.azure_configured && (
        <div className="alert-warning text-xs">
          No Azure Blob credentials saved. Student attachments on this tab cannot be
          viewed or downloaded until connection string + container name are added.
        </div>
      )}
      <div className="flex flex-wrap gap-2 pt-1">
        <Link to={`/admin/tabs/${tab.id}/knowledge`} className="btn-secondary text-xs">
          <BookOpen size={12} /> Knowledge
        </Link>
        <Link to={`/admin/tabs/${tab.id}/sync`} className="btn-secondary text-xs">
          <Zap size={12} /> Sync
        </Link>
        <Link to={`/admin/tabs/${tab.id}/test-tickets`} className="btn-secondary text-xs">
          <FlaskConical size={12} /> Test tickets
        </Link>
        <button type="button" onClick={onValidate} className="btn-secondary text-xs">
          <RefreshCw size={12} /> Revalidate
        </button>
        <button
          type="button"
          onClick={onRegenerate}
          className="btn-secondary text-xs"
          title="Regenerate AI drafts for every Pending ticket in this tab"
        >
          <Sparkles size={12} /> Regenerate AI Responses
        </button>
        <button type="button" onClick={onToggle} className="btn-secondary text-xs">
          {tab.read_only ? (
            <>
              <UnlockKeyhole size={12} /> Enable writes
            </>
          ) : (
            <>
              <Lock size={12} /> Lock (read-only)
            </>
          )}
        </button>
        <div className="ml-auto flex gap-2">
          <button
            type="button"
            onClick={onEdit}
            className="btn-secondary text-xs !px-2"
            aria-label="Edit tab"
            title="Edit tab"
          >
            <Pencil size={12} />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="btn-danger text-xs !px-2"
            aria-label="Delete tab"
            title="Delete tab"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>
    </li>
  );
}

function SchemaInvalidPanel({ report }: { report: Record<string, unknown> | null }) {
  if (!report) {
    return (
      <div className="alert-warning text-xs">
        Schema not validated yet. Click <strong>Revalidate</strong> to check the govt table
        structure against the required column contract.
      </div>
    );
  }
  const reason = report.reason as string | undefined;
  if (reason === "table_not_found") {
    return (
      <div className="alert-danger text-xs">
        Table <code>{String(report.table_name ?? "")}</code> does not exist in the connected DB.
        Fix the table name (Edit) or check the credentials point at the right database.
      </div>
    );
  }
  const missing = (report.columns_missing as string[] | undefined) ?? [];
  const mismatched =
    (report.columns_mismatched as Array<{
      column: string;
      issue: string;
      expected: Record<string, unknown>;
      actual: Record<string, unknown>;
    }> | undefined) ?? [];
  const extras = (report.columns_extra as string[] | undefined) ?? [];

  return (
    <details className="alert-danger text-xs">
      <summary className="cursor-pointer font-medium">
        Why is this schema invalid?{" "}
        <span className="text-[10px] font-normal opacity-75">
          {missing.length > 0 && `${missing.length} missing`}
          {missing.length > 0 && mismatched.length > 0 && " · "}
          {mismatched.length > 0 && `${mismatched.length} mismatched`}
        </span>
      </summary>
      <div className="mt-2 space-y-2">
        {missing.length > 0 && (
          <div>
            <p className="font-semibold">Missing columns:</p>
            <ul className="list-disc pl-5">
              {missing.map((c) => (
                <li key={c}>
                  <code>{c}</code>
                </li>
              ))}
            </ul>
          </div>
        )}
        {mismatched.length > 0 && (
          <div>
            <p className="font-semibold">Column mismatches:</p>
            <ul className="list-disc pl-5 space-y-1">
              {mismatched.map((m, i) => (
                <li key={i}>
                  <code>{m.column}</code> – <em>{m.issue}</em>
                  <div className="text-[10px] opacity-75 font-mono">
                    expected: {JSON.stringify(m.expected)}
                  </div>
                  <div className="text-[10px] opacity-75 font-mono">
                    actual:{"   "}
                    {JSON.stringify(m.actual)}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
        {extras.length > 0 && (
          <div className="opacity-75">
            <p className="font-semibold">Extra columns (ignored, not a problem):</p>
            <p className="font-mono break-all">{extras.join(", ")}</p>
          </div>
        )}
      </div>
    </details>
  );
}

function AzureChip({ tab }: { tab: TabOut }) {
  if (!tab.azure_configured) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400 px-2 py-0.5 text-[11px] font-medium"
        title="No Azure Blob creds – student attachments cannot be viewed for this tab"
      >
        <CloudOff size={11} /> No attachments
      </span>
    );
  }
  if (tab.azure_valid === true) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300 px-2 py-0.5 text-[11px] font-medium"
        title={`Container: ${tab.azure_container_name ?? "–"} · last checked: ${tab.azure_last_checked ?? "–"}`}
      >
        <Cloud size={11} /> Attachments OK
      </span>
    );
  }
  if (tab.azure_valid === false) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 px-2 py-0.5 text-[11px] font-medium"
        title="Azure container unreachable"
      >
        <CloudOff size={11} /> Attachments error
      </span>
    );
  }
  // azure_valid === null → configured but never checked
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 px-2 py-0.5 text-[11px] font-medium"
      title="Attachments not validated – click Revalidate to check"
    >
      <Cloud size={11} /> Attachments unchecked
    </span>
  );
}

function AzureInvalidPanel({ report }: { report: Record<string, unknown> | null }) {
  if (!report) {
    return (
      <div className="alert-warning text-xs">
        Attachments not validated yet. Click <strong>Revalidate</strong> to probe the Azure
        Blob container.
      </div>
    );
  }
  const reason = report.reason as string | undefined;
  const message = (report.message as string | undefined) ?? null;
  const container = report.container_name as string | undefined;

  if (reason === "not_configured") {
    return (
      <div className="alert-warning text-xs">
        {message ?? "No Azure Blob credentials saved for this tab."}
      </div>
    );
  }

  return (
    <details className="alert-danger text-xs">
      <summary className="cursor-pointer font-medium">
        Why is the attachment store not connected?
      </summary>
      <div className="mt-2 space-y-1">
        {container && (
          <p>
            Container: <code>{container}</code>
          </p>
        )}
        {reason && (
          <p>
            Reason: <code>{reason}</code>
          </p>
        )}
        {message && <p className="font-mono break-all opacity-80">{message}</p>}
        <p className="opacity-75 pt-1">
          Fix: open <strong>Edit</strong>, re-enter the Azure connection string + container
          name, save, then click <strong>Revalidate</strong>.
        </p>
      </div>
    </details>
  );
}
